import React, { memo } from 'react';
import { Handle, Position } from 'reactflow';
import { Zap, MessageSquare, Image, Mic, Video, FileText, List, Clock, GitBranch, StopCircle } from 'lucide-react';

const nodeColors = {
  trigger: { bg: '#22c55e', border: '#16a34a', text: '#fff' },
  text_message: { bg: '#0f172a', border: '#1e293b', text: '#fff' },
  image_message: { bg: '#3b82f6', border: '#2563eb', text: '#fff' },
  audio_message: { bg: '#8b5cf6', border: '#7c3aed', text: '#fff' },
  video_message: { bg: '#f97316', border: '#ea580c', text: '#fff' },
  document_message: { bg: '#ef4444', border: '#dc2626', text: '#fff' },
  quick_reply: { bg: '#06b6d4', border: '#0891b2', text: '#fff' },
  delay: { bg: '#eab308', border: '#ca8a04', text: '#fff' },
  condition: { bg: '#ec4899', border: '#db2777', text: '#fff' },
  end: { bg: '#64748b', border: '#475569', text: '#fff' },
};

const nodeIcons = {
  trigger: Zap,
  text_message: MessageSquare,
  image_message: Image,
  audio_message: Mic,
  video_message: Video,
  document_message: FileText,
  quick_reply: List,
  delay: Clock,
  condition: GitBranch,
  end: StopCircle,
};

const nodeLabels = {
  trigger: 'Trigger',
  text_message: 'Text Message',
  image_message: 'Image',
  audio_message: 'Audio',
  video_message: 'Video',
  document_message: 'Document',
  quick_reply: 'Quick Reply',
  delay: 'Delay',
  condition: 'Condition',
  end: 'End',
};

function BaseNode({ data, selected, id }) {
  const nodeType = data.node_type || 'text_message';
  const colors = nodeColors[nodeType] || nodeColors.text_message;
  const Icon = nodeIcons[nodeType] || MessageSquare;
  const label = nodeLabels[nodeType] || 'Node';
  const config = data.config || {};

  const isQuickReply = nodeType === 'quick_reply';
  const isCondition = nodeType === 'condition';
  const isTrigger = nodeType === 'trigger';
  const isEnd = nodeType === 'end';

  const options = config.options || [];

  return (
    <div
      className={`rounded-lg shadow-lg min-w-[200px] max-w-[260px] transition-all ${selected ? 'ring-2 ring-white ring-offset-2 ring-offset-slate-900' : ''}`}
      style={{ background: colors.bg, borderColor: colors.border, borderWidth: 2, borderStyle: 'solid' }}
      data-testid={`flow-node-${id}`}
    >
      {/* Input handle (not on trigger) */}
      {!isTrigger && (
        <Handle type="target" position={Position.Top} className="!w-3 !h-3 !bg-white !border-2" style={{ borderColor: colors.border }} />
      )}

      {/* Header */}
      <div className="px-3 py-2 flex items-center gap-2" style={{ borderBottom: `1px solid ${colors.border}` }}>
        <Icon className="w-4 h-4 shrink-0" style={{ color: colors.text }} />
        <span className="text-xs font-bold uppercase tracking-wider" style={{ color: colors.text }}>{label}</span>
      </div>

      {/* Title */}
      <div className="px-3 py-2">
        <p className="text-sm font-medium truncate" style={{ color: colors.text }}>
          {data.title || label}
        </p>
        {/* Preview */}
        {config.content && (
          <p className="text-[10px] mt-1 opacity-70 truncate" style={{ color: colors.text }}>
            {config.content.substring(0, 60)}{config.content.length > 60 ? '...' : ''}
          </p>
        )}
        {config.media_url && (
          <p className="text-[10px] mt-1 opacity-70 truncate" style={{ color: colors.text }}>
            [Media attached]
          </p>
        )}
        {isTrigger && (
          <p className="text-[10px] mt-1 opacity-70" style={{ color: colors.text }}>
            {config.trigger_type === 'any_message' ? 'Any message' : `Keyword: ${config.keyword || '...'}`}
          </p>
        )}
        {nodeType === 'delay' && (
          <p className="text-[10px] mt-1 opacity-70" style={{ color: colors.text }}>
            {config.delay_amount || 5} {config.delay_unit || 'seconds'}
          </p>
        )}
        {isEnd && config.message && (
          <p className="text-[10px] mt-1 opacity-70 truncate" style={{ color: colors.text }}>
            {config.message.substring(0, 40)}
          </p>
        )}
      </div>

      {/* Quick Reply options with separate handles */}
      {isQuickReply && options.length > 0 && (
        <div className="border-t" style={{ borderColor: colors.border }}>
          {options.map((opt, idx) => (
            <div key={opt.id || idx} className="px-3 py-1 flex items-center justify-between relative text-[10px]" style={{ color: colors.text, borderBottom: idx < options.length - 1 ? `1px solid ${colors.border}` : 'none' }}>
              <span className="truncate">{opt.label || `Option ${idx + 1}`}</span>
              <Handle
                type="source"
                position={Position.Right}
                id={opt.id || `opt_${idx}`}
                className="!w-2.5 !h-2.5 !bg-white !border-2"
                style={{ borderColor: colors.border, top: 'auto', right: -5 }}
              />
            </div>
          ))}
        </div>
      )}

      {/* Condition branches */}
      {isCondition && (config.branches || []).length > 0 && (
        <div className="border-t" style={{ borderColor: colors.border }}>
          {(config.branches || []).map((br, idx) => (
            <div key={br.id || idx} className="px-3 py-1 flex items-center justify-between relative text-[10px]" style={{ color: colors.text }}>
              <span className="truncate">{br.label || `Branch ${idx + 1}`}</span>
              <Handle
                type="source"
                position={Position.Right}
                id={br.id || `br_${idx}`}
                className="!w-2.5 !h-2.5 !bg-white !border-2"
                style={{ borderColor: colors.border, top: 'auto', right: -5 }}
              />
            </div>
          ))}
        </div>
      )}

      {/* Default output handle (not on end, quick_reply, condition) */}
      {!isEnd && !isQuickReply && !isCondition && (
        <Handle type="source" position={Position.Bottom} className="!w-3 !h-3 !bg-white !border-2" style={{ borderColor: colors.border }} />
      )}
    </div>
  );
}

export const TriggerNode = memo((props) => <BaseNode {...props} />);
export const TextMessageNode = memo((props) => <BaseNode {...props} />);
export const ImageMessageNode = memo((props) => <BaseNode {...props} />);
export const AudioMessageNode = memo((props) => <BaseNode {...props} />);
export const VideoMessageNode = memo((props) => <BaseNode {...props} />);
export const DocumentMessageNode = memo((props) => <BaseNode {...props} />);
export const QuickReplyNode = memo((props) => <BaseNode {...props} />);
export const DelayNode = memo((props) => <BaseNode {...props} />);
export const ConditionNode = memo((props) => <BaseNode {...props} />);
export const EndNode = memo((props) => <BaseNode {...props} />);

export const nodeTypes = {
  trigger: TriggerNode,
  text_message: TextMessageNode,
  image_message: ImageMessageNode,
  audio_message: AudioMessageNode,
  video_message: VideoMessageNode,
  document_message: DocumentMessageNode,
  quick_reply: QuickReplyNode,
  delay: DelayNode,
  condition: ConditionNode,
  end: EndNode,
};

export { nodeColors, nodeIcons, nodeLabels };
